import {
  CheckCircle2,
  Circle,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useState, useMemo } from "react";

import {
  parseMarkdownTasks,
  filterTasksByStatus,
  searchTasks,
  groupTasksBySection,
  type MarkdownTask,
  type ParsedMarkdown,
} from "@/utils/markdownParser";

interface TaskListViewerProps {
  markdownContent: string;
  fileName: string;
}

export function TaskListViewer({
  markdownContent,
  fileName,
}: TaskListViewerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "completed" | "pending"
  >("all");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  // Parse markdown
  const parsedData: ParsedMarkdown = useMemo(
    () => parseMarkdownTasks(markdownContent),
    [markdownContent],
  );

  // Apply filters
  const filteredTasks = useMemo(() => {
    let tasks = parsedData.allTasks;
    tasks = filterTasksByStatus(tasks, statusFilter);
    tasks = searchTasks(tasks, searchQuery);
    return tasks;
  }, [parsedData.allTasks, statusFilter, searchQuery]);

  // Group by section
  const groupedTasks = useMemo(
    () => groupTasksBySection(parsedData.sections),
    [parsedData.sections],
  );

  const toggleSection = (sectionPath: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionPath)) {
      newExpanded.delete(sectionPath);
    } else {
      newExpanded.add(sectionPath);
    }
    setExpandedSections(newExpanded);
  };

  const expandAll = () => {
    setExpandedSections(new Set(Array.from(groupedTasks.keys())));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {parsedData.title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{fileName}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">
              {parsedData.progressPercentage}%
            </div>
            <div className="text-sm text-gray-500">
              {parsedData.completedTasks} / {parsedData.totalTasks} tasks
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${parsedData.progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 h-5 w-5" />
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as "all" | "completed" | "pending",
                )
              }
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Tasks ({parsedData.totalTasks})</option>
              <option value="pending">
                Pending ({parsedData.totalTasks - parsedData.completedTasks})
              </option>
              <option value="completed">
                Completed ({parsedData.completedTasks})
              </option>
            </select>
          </div>
        </div>

        {/* Expand/Collapse All */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={expandAll}
            className="text-sm px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="text-sm px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Task List by Section */}
      <div className="space-y-4">
        {Array.from(groupedTasks.entries()).map(
          ([sectionPath, sectionTasks]) => {
            const visibleTasks = sectionTasks.filter((task) =>
              filteredTasks.includes(task),
            );
            if (visibleTasks.length === 0) return null;

            const isExpanded = expandedSections.has(sectionPath);
            const completedCount = visibleTasks.filter(
              (t) => t.completed,
            ).length;

            return (
              <div
                key={sectionPath}
                className="bg-white rounded-lg shadow-sm border border-gray-200"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(sectionPath)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">
                      {sectionPath}
                    </h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      {completedCount} / {visibleTasks.length} completed
                    </span>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(completedCount / visibleTasks.length) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </button>

                {/* Task Items */}
                {isExpanded && (
                  <div className="px-6 pb-4 space-y-2">
                    {visibleTasks.map((task) => (
                      <TaskItem key={task.id} task={task} />
                    ))}
                  </div>
                )}
              </div>
            );
          },
        )}

        {filteredTasks.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">
              No tasks found matching your filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function TaskItem({ task }: { task: MarkdownTask }) {
  return (
    <div className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-md transition-colors">
      <div className="flex-shrink-0 mt-0.5">
        {task.completed ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <Circle className="h-5 w-5 text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm ${
            task.completed ? "text-gray-500 line-through" : "text-gray-900"
          }`}
        >
          {task.text}
        </p>
        {task.context.length > 0 && (
          <div className="mt-1 text-xs text-gray-400">
            Context: {task.context.slice(-2).join(" → ")}
          </div>
        )}
        <div className="mt-1 text-xs text-gray-400">Line {task.line}</div>
      </div>
    </div>
  );
}
